import { useState, useRef, useLayoutEffect } from 'react';
import { type Page, type PortalSettings, type Section as SectionType } from '@gorilla/portal-shared/src/schemas';
import { type SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { getSectionDefaultValues, coreSectionDescriptorsByType } from '@gorilla/portal-shared/src/core-sections';
import {
  Dialog,
  DialogContentContainer,
  Menu,
  MenuItem,
  Box,
  Flex,
  Text,
  Divider,
  IconButton,
  Button,
  Tooltip,
  Icon,
  Badge,
  MenuDivider,
  Label,
} from '@vibe/core';
import { shift } from '@floating-ui/react-dom';
import { Heading } from '@vibe/core';
import {
  Broadcast,
  Apps,
  AddSmall,
  Delete,
  DropdownChevronUp,
  DropdownChevronDown,
  Settings,
  Workflow,
  NewTab,
  Menu as MenuIcon,
  Duplicate,
  MoveArrowRight,
  Hide,
  Show,
} from '@vibe/icons';
import { type Board as BoardType } from '@gorilla/common/src/lib/monday-api/api';
import { ConditionsModal } from './ConditionsModal';
import { createRandomId } from '../lib/utils';
import { Section } from './Section';
import { ConfirmModal } from './ConfirmModal';
import { SectionsModal } from './SectionsModal';
import { PagePickerModal } from './PagePickerModal';
import { OnboardingDialog } from './OnboardingDialog';
import { PageSettingsModal } from './PageSettingsModal';
import { EditablePageName } from './EditablePageName';

type PageProps = {
  clientBoard: BoardType;
  clientBoardItems: any;
  portalSettings: PortalSettings;
  onChangePortalSettings: (portalSettings: PortalSettings) => void;
  page: Page;
  onDelete: () => void;
  onChange: (newPage: Page) => void;
  setCustomizationModal: any;
  customizationModal: any;
  onManageBoardSourcesClick: () => void;
  onEditBoardSourceClick: (boardSourceId: string) => void;
  onShowPlaceholdersModalClick: () => void;
  clientBoardSubitemsBoardId: number | null;
  screenMode: 'small' | 'medium' | 'large';
  hasUnpublishedChanges: boolean | undefined;
  openSectionIds: string[];
  toggleSectionOpen: (section: SectionType, targetIsOpen?: boolean) => void;
  showOnboardingDialogTips: boolean;
  setShowOnboardingDialogTips: (show: boolean) => void;
  onDismiss?: () => void;
  onPreviewPortal?: () => void;
};

export function Page({
  page,
  onChange,
  onDelete,
  portalSettings,
  onChangePortalSettings,
  onManageBoardSourcesClick,
  onEditBoardSourceClick,
  onShowPlaceholdersModalClick,
  clientBoardSubitemsBoardId,
  clientBoard,
  clientBoardItems,
  screenMode,
  hasUnpublishedChanges,
  openSectionIds,
  toggleSectionOpen,
  showOnboardingDialogTips,
  setShowOnboardingDialogTips,
  onDismiss,
  onPreviewPortal,
}: PageProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [showSectionsModal, setShowSectionsModal] = useState(false);
  const [confirmDeletePageModalOpen, setConfirmDeletePageModalOpen] = useState(false);
  const [showPageConditionsModal, setPageShowConditionsModal] = useState(false);
  const [pageModalView, setPageModalView] = useState<'settings' | 'delete' | null>(null);
  const [pendingScrollSectionId, setPendingScrollSectionId] = useState<string | null>(null);
  const [advancedMenuOpen, setAdvancedMenuOpen] = useState(false);
  const [onboardingDialogStep, setOnboardingDialogStep] = useState<number>(1);
  const [newSectionIdsAdded, setNewSectionIdsAdded] = useState<string[]>([]);
  const [sectionOptionsOpen, setSectionOptionsOpen] = useState<string>();
  const [sectionToMove, setSectionToMove] = useState<SectionType | null>(null);
  const onboardingDialogStepsTotal = 3;

  function addSection(sectionDescriptor: SectionDescriptor) {
    const section = getSectionDefaultValues(createRandomId(), sectionDescriptor);

    const newPage: Page = {
      ...page,
      sections: [...page.sections, section],
    };

    onChange(newPage);

    // make sure new section starts open
    toggleSectionOpen(section, true);

    // tell the effect: once DOM is updated, scroll to this section
    setPendingScrollSectionId(section.id);

    setNewSectionIdsAdded((prev) => [...prev, section.id]);
  }

  // Scroll to a newly added section once it exists in the DOM
  useLayoutEffect(() => {
    if (!pendingScrollSectionId) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const scroll = () => {
      const el = document.getElementById(pendingScrollSectionId);
      if (!el) return; // if it's still not there, just bail

      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const delta = elRect.top - containerRect.top;
      const targetTop = container.scrollTop + delta;

      container.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth',
      });

      // clear so we don’t re-scroll on every render
      setPendingScrollSectionId(null);
    };

    // wait for layout of the new section
    requestAnimationFrame(scroll);
  }, [pendingScrollSectionId, page.sections.length]);

  function scrollToTop(behavior: ScrollBehavior = 'smooth') {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: 0,
      behavior,
    });
  }

  function scrollToBottom(behavior: ScrollBehavior = 'smooth') {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  }

  return (
    <>
      {showSectionsModal ? (
        <SectionsModal
          addSection={addSection}
          onClose={() => {
            setShowSectionsModal(false);
          }}
        />
      ) : null}
      {sectionToMove ? (
        <PagePickerModal
          isOpen
          portalSettings={portalSettings}
          excludePageId={page.id}
          title="Move section to page"
          icon={MoveArrowRight}
          onClose={() => setSectionToMove(null)}
          onSelect={(targetPage) => {
            onChangePortalSettings({
              ...portalSettings,
              pages: portalSettings.pages.map((p) => {
                if (p.id === page.id) {
                  return {
                    ...p,
                    sections: p.sections.filter((s) => s.id !== sectionToMove.id),
                  };
                }
                if (p.id === targetPage.id) {
                  return { ...p, sections: [...p.sections, sectionToMove] };
                }
                return p;
              }),
            });
            setSectionToMove(null);
          }}
        />
      ) : null}
      {confirmDeletePageModalOpen ? (
        <ConfirmModal
          isOpen={confirmDeletePageModalOpen}
          isConfirming={false}
          headline="Delete page"
          text="Are you sure you want to delete this page?"
          info="By deleting, this page including all content sections are irretrievably removed from your portal configuration."
          confirmButtonLabel="Delete page"
          onConfirm={async () => {
            onDelete();
            setConfirmDeletePageModalOpen(false);
          }}
          onClose={() => {
            setConfirmDeletePageModalOpen(false);
          }}
        />
      ) : null}
      {clientBoard && showPageConditionsModal ? (
        <ConditionsModal
          board={clientBoard}
          conditions={page.conditions || []}
          conditionsOperator={page.conditionsOperator}
          isOpen={true}
          onClose={(newConditions, newOperator) => {
            if (newConditions !== null) {
              onChange({
                ...page,
                conditions: newConditions,
                conditionsOperator: newOperator,
              });
            }

            setPageShowConditionsModal(false);
          }}
          isButtonCondition={false}
        />
      ) : null}
      <Box>
        <Box
          className={
            screenMode === 'small'
              ? 'portalmanager__content__breadcrumb portalmanager__content__breadcrumb--narrow'
              : screenMode === 'medium'
                ? 'portalmanager__content__breadcrumb portalmanager__content__breadcrumb--medium'
                : 'portalmanager__content__breadcrumb'
          }
        >
          <Flex
            style={{
              height: 'inherit',
            }}
          >
            <OnboardingDialog
              dialogPosition="right"
              offsetX={-15}
              offsetY={45}
              showOnboardingDialogTips={showOnboardingDialogTips}
              setShowOnboardingDialogTips={setShowOnboardingDialogTips}
              dialogStep={1}
              onboardingStepsDialogTotal={onboardingDialogStepsTotal}
              onboardingDialogStep={onboardingDialogStep}
              setOnboardingDialogStep={setOnboardingDialogStep}
              onDismiss={onDismiss}
              title="Create portal pages"
              description="Here you add pages to your client portal to organize your content and guide your clients with a clear and engaging navigation."
              onStepChange={(next, current) => {
                if (current === 1 && next === 2) {
                  requestAnimationFrame(() => scrollToBottom());
                } else if (current === 2 && next === 1) {
                  requestAnimationFrame(() => scrollToTop());
                }
              }}
              clientBoardItems={clientBoardItems}
              clientBoard={clientBoard}
            >
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                }}
              >
                &nbsp;
              </div>
            </OnboardingDialog>
            <OnboardingDialog
              dialogPosition="bottom"
              offsetX={-28}
              offsetY={0}
              showOnboardingDialogTips={showOnboardingDialogTips}
              setShowOnboardingDialogTips={setShowOnboardingDialogTips}
              dialogStep={3}
              onboardingStepsDialogTotal={onboardingDialogStepsTotal}
              onboardingDialogStep={onboardingDialogStep}
              setOnboardingDialogStep={setOnboardingDialogStep}
              onDismiss={onDismiss}
              title="Preview your portal"
              description="See exactly what your clients will experience. This preview shows your current draft and impersonates your first client by default."
              onStepChange={(next, current) => {
                if (current === 3 && next === 2) {
                  requestAnimationFrame(() => scrollToBottom());
                }
              }}
              onFinalStepDone={() => {
                onPreviewPortal?.();
              }}
              clientBoardItems={clientBoardItems}
              clientBoard={clientBoard}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: screenMode === 'small' ? '185px' : '225px',
                }}
              >
                &nbsp;
              </div>
            </OnboardingDialog>
            <Flex
              gap={10}
              style={{
                flex: 1,
                height: 'inherit',
              }}
            >
              {page.menu !== 'footer' && !page.icon ? (
                <Tooltip content="Page settings" position="top" showOnDialogEnter={false}>
                  <IconButton
                    icon={Settings}
                    kind="secondary"
                    size="small"
                    color="primary"
                    onClick={() => {
                      setPageModalView('settings');
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip content="Page settings" position="top" showOnDialogEnter={false}>
                  <Badge
                    // @ts-ignore
                    color="primary"
                  >
                    <IconButton
                      icon={Settings}
                      kind="secondary"
                      size="small"
                      color="primary"
                      onClick={() => {
                        setPageModalView('settings');
                      }}
                    />
                  </Badge>
                </Tooltip>
              )}

              {/* a definite width (flex: 1) keeps EditableHeading from truncating: sized to its content, it comes out a few px short */}
              <div
                style={{
                  flex: '1 1 0%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minWidth: 0,
                  marginRight: '5px',
                  marginLeft: '8px',
                  maxWidth: screenMode === 'small' ? 'calc(100vw - 480px)' : 'calc(100vw - 770px)',
                }}
              >
                {/* only the name changes: the alias (page id) is the client-facing URL and stays in page settings */}
                <EditablePageName name={page.name} onChange={(name) => onChange({ ...page, name })} />
                {page.hidden && <Label text="Hidden" color="dark" />}
              </div>
            </Flex>
            <Box style={{ overflow: 'visible', marginLeft: 'auto', height: '100%' }}>
              <Flex gap={12} align="stretch" style={{ height: '100%' }}>
                <Flex gap={12} align="center">
                  {page.conditions && page.conditions.length ? (
                    <Tooltip content="Page conditions" position="top" showOnDialogEnter={false}>
                      <Badge
                        // @ts-ignore
                        color="primary"
                      >
                        <IconButton
                          icon={Workflow}
                          kind="secondary"
                          size="small"
                          onClick={() => {
                            setPageShowConditionsModal(true);
                          }}
                        />
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Tooltip content="Page conditions" position="top" showOnDialogEnter={false}>
                      <IconButton
                        icon={Workflow}
                        kind="secondary"
                        size="small"
                        onClick={() => {
                          setPageShowConditionsModal(true);
                        }}
                      />
                    </Tooltip>
                  )}
                </Flex>

                <Flex gap={0} style={{ marginRight: '5px' }}>
                  <Dialog
                    content={
                      <DialogContentContainer
                        size="none"
                        style={{
                          overflow: 'hidden',
                        }}
                      >
                        <Menu size="xs">
                          <MenuItem
                            title="Add section to page"
                            icon={NewTab}
                            onClick={() => {
                              setAdvancedMenuOpen(false);
                              setShowSectionsModal(true);
                            }}
                            className="customMenuItem"
                          />
                          <MenuDivider />
                          <MenuItem
                            title={page.hidden ? 'Show page' : 'Hide page'}
                            icon={page.hidden ? Show : Hide}
                            onClick={() => {
                              setAdvancedMenuOpen(false);
                              onChange({ ...page, hidden: !page.hidden });
                            }}
                            className="customMenuItem"
                          />
                          <MenuItem
                            title="Delete page"
                            icon={Delete}
                            onClick={() => {
                              setAdvancedMenuOpen(false);
                              setConfirmDeletePageModalOpen(true);
                            }}
                            className="customMenuItem"
                          />
                          <MenuDivider />
                          <MenuItem
                            title="Board sources"
                            icon={Broadcast}
                            onClick={() => {
                              setAdvancedMenuOpen(false);
                              onManageBoardSourcesClick();
                            }}
                            className="customMenuItem"
                          />
                          <MenuItem
                            title="Placeholders"
                            icon={Apps}
                            onClick={() => {
                              setAdvancedMenuOpen(false);
                              onShowPlaceholdersModalClick();
                            }}
                            className="customMenuItem"
                          />
                        </Menu>
                      </DialogContentContainer>
                    }
                    middleware={[shift({ mainAxis: false })]}
                    showTrigger={[]}
                    open={advancedMenuOpen}
                    instantShowAndHide
                    tooltip
                    zIndex={3000}
                    moveBy={{ main: 5, secondary: -65 }}
                    position="bottom"
                    onClickOutside={() => {
                      setAdvancedMenuOpen(false);
                    }}
                  >
                    <IconButton
                      icon={MenuIcon}
                      kind="secondary"
                      size="small"
                      color="primary"
                      active={advancedMenuOpen}
                      onClick={() => {
                        setAdvancedMenuOpen(!advancedMenuOpen);
                      }}
                    />
                  </Dialog>
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Box>

        {/* PAGE SETTINGS */}
        {pageModalView === 'settings' ? (
          <PageSettingsModal
            page={page}
            existingAliases={portalSettings.pages.filter((p) => p.id !== page.id).map((p) => p.id)}
            onSave={(updatedPage) => {
              onChange(updatedPage);
            }}
            onClose={() => {
              setPageModalView(null);
            }}
          />
        ) : null}

        <div
          ref={scrollContainerRef}
          style={{
            overflow: 'hidden',
            overflowY: 'auto',
            marginTop: '72px',
            height: '100%',
            maxHeight: 'calc(100vh - 146px)',
          }}
        >
          <Box>
            {page.sections.length ? (
              <Box className="section_level_1">
                {page.sections.map((section, idx) => {
                  let sectionDescriptor = coreSectionDescriptorsByType[section.type];
                  const sectionIsOpen = openSectionIds.includes(section.id);

                  const sectionContent = (
                    <Section
                      key={section.id}
                      clientBoard={clientBoard}
                      section={section}
                      sectionDescriptor={sectionDescriptor}
                      portalSettings={portalSettings}
                      onEditBoardSourceClick={onEditBoardSourceClick}
                      onShowPlaceholdersModalClick={onShowPlaceholdersModalClick}
                      clientBoardSubitemsBoardId={clientBoardSubitemsBoardId}
                      onChangePortalSettings={onChangePortalSettings}
                      onChange={(newSection) => {
                        onChange({
                          ...page,
                          sections: page.sections.map((s) => (s.id === section.id ? newSection : s)),
                        });
                      }}
                      isOpen={openSectionIds.includes(section.id)}
                      onToggleSectionOpen={() => {
                        toggleSectionOpen(section);
                      }}
                      screenMode={screenMode}
                      isNewSection={newSectionIdsAdded.includes(section.id)}
                      pageId={page.id}
                    />
                  );

                  return (
                    <Box
                      key={section.id}
                      id={section.id}
                      style={{
                        overflow: 'visible',
                        scrollMarginTop: idx === 0 ? '0px' : '144px',
                      }}
                      className="section__wrapper section_level_2"
                    >
                      <Box
                        marginTop={screenMode !== 'small' ? 'xxl' : 'xl'}
                        marginEnd={screenMode !== 'small' ? 'xxl' : 'xl'}
                        marginStart={screenMode !== 'small' ? 'xxl' : 'xl'}
                        border={true}
                        borderColor="layoutBorderColor"
                        rounded="small"
                        backgroundColor="primaryBackgroundColor"
                        shadow="xs"
                        style={{
                          overflow: 'visible',
                          position: 'relative',
                        }}
                        className="section_level_3"
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: '17px',
                            right: '50px',
                          }}
                        >
                          <Dialog
                            content={
                              <DialogContentContainer
                                size="none"
                                style={{
                                  overflow: 'hidden',
                                }}
                              >
                                <Menu size="xs">
                                  <MenuItem
                                    title="Duplicate section"
                                    icon={Duplicate}
                                    onClick={() => {
                                      setSectionOptionsOpen(undefined);
                                      const clonedSection = {
                                        ...structuredClone(section),
                                        id: createRandomId(),
                                      };
                                      onChange({
                                        ...page,
                                        sections: [...page.sections.slice(0, idx + 1), clonedSection, ...page.sections.slice(idx + 1)],
                                      });
                                      toggleSectionOpen(clonedSection, true);
                                      setPendingScrollSectionId(clonedSection.id);
                                    }}
                                    className="customMenuItem"
                                  />
                                  <MenuItem
                                    title={section.hidden ? 'Show section' : 'Hide section'}
                                    icon={section.hidden ? Show : Hide}
                                    onClick={() => {
                                      setSectionOptionsOpen(undefined);
                                      onChange({
                                        ...page,
                                        sections: page.sections.map((s) => (s.id === section.id ? { ...s, hidden: !s.hidden } : s)),
                                      });
                                    }}
                                    className="customMenuItem"
                                  />
                                  <MenuItem
                                    title="Delete section"
                                    icon={Delete}
                                    onClick={() => {
                                      setSectionOptionsOpen(undefined);
                                      onChange({
                                        ...page,
                                        sections: page.sections.filter((s) => s.id !== section.id),
                                      });
                                    }}
                                    className="customMenuItem"
                                  />
                                  <MenuDivider />
                                  <MenuItem
                                    title="Move to page"
                                    icon={MoveArrowRight}
                                    disabled={portalSettings.pages.length < 2}
                                    onClick={() => {
                                      setSectionOptionsOpen(undefined);
                                      setSectionToMove(section);
                                    }}
                                    className="customMenuItem"
                                  />
                                </Menu>
                              </DialogContentContainer>
                            }
                            middleware={[shift({ mainAxis: false })]}
                            showTrigger={[]}
                            open={sectionOptionsOpen === section.id}
                            instantShowAndHide
                            hideWhenReferenceHidden
                            zIndex={3000}
                            moveBy={{ main: 10, secondary: -60 }}
                            position="bottom"
                            tooltip
                            onClickOutside={() => {
                              setSectionOptionsOpen(undefined);
                            }}
                          >
                            <IconButton
                              icon={MenuIcon}
                              kind="secondary"
                              size="small"
                              color="primary"
                              active={sectionOptionsOpen === section.id}
                              onClick={() => {
                                setSectionOptionsOpen(section.id);
                              }}
                            />
                          </Dialog>
                        </div>

                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            borderLeft: '1px solid #d0d4e4',
                          }}
                        >
                          <Flex direction="row" style={{ alignItems: 'stretch' }}>
                            <Flex direction="column">
                              <IconButton
                                className="portalmanager__content__sectionbutton"
                                icon={DropdownChevronUp}
                                kind="tertiary"
                                size="small"
                                disabled={idx > 0 ? false : true}
                                //tooltipContent="Move up"
                                tooltipProps={{ position: 'top', showOnDialogEnter: false }}
                                hideTooltip={idx === 0}
                                onClick={() => {
                                  onChange({
                                    ...page,
                                    sections: page.sections.map((s, i) => {
                                      if (i === idx) {
                                        return page.sections[idx - 1];
                                      } else if (i === idx - 1) {
                                        return page.sections[idx];
                                      } else {
                                        return s;
                                      }
                                    }),
                                  });
                                }}
                              />

                              <Divider withoutMargin />

                              <IconButton
                                className="portalmanager__content__sectionbutton"
                                icon={DropdownChevronDown}
                                kind="tertiary"
                                size="small"
                                disabled={idx < page.sections.length - 1 ? false : true}
                                //tooltipContent="Move down"
                                tooltipProps={{ position: 'top', showOnDialogEnter: false }}
                                hideTooltip={idx === page.sections.length - 1}
                                onClick={() => {
                                  onChange({
                                    ...page,
                                    sections: page.sections.map((s, i) => {
                                      if (i === idx) {
                                        return page.sections[idx + 1];
                                      } else if (i === idx + 1) {
                                        return page.sections[idx];
                                      } else {
                                        return s;
                                      }
                                    }),
                                  });
                                }}
                              />
                            </Flex>
                          </Flex>
                        </div>
                        <Box
                          className="section__wrapper--inside section_level_4"
                          style={{
                            borderBottomRightRadius: sectionIsOpen ? '3px' : '0px',
                            borderBottomLeftRadius: sectionIsOpen ? '3px' : '0px',
                            borderBottom: sectionIsOpen ? '2px solid #d0d4e4' : undefined,
                          }}
                        >
                          {sectionContent}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                <Box paddingTop="xxxl" paddingBottom="xxxl" paddingEnd="xxl" paddingStart="xxl">
                  <Flex direction="column" justify="center" align="center" gap={12}>
                    <Flex direction="column" gap={20}>
                      <Flex direction="column" gap={24}>
                        <OnboardingDialog
                          dialogPosition="top"
                          offsetX={15}
                          offsetY={0}
                          showOnboardingDialogTips={showOnboardingDialogTips}
                          setShowOnboardingDialogTips={setShowOnboardingDialogTips}
                          dialogStep={2}
                          onboardingStepsDialogTotal={onboardingDialogStepsTotal}
                          onboardingDialogStep={onboardingDialogStep}
                          setOnboardingDialogStep={setOnboardingDialogStep}
                          onDismiss={onDismiss}
                          title="Add content sections"
                          description="Here you add sections to your pages to display different types of content to your clients. Just mix and match as you like."
                          onStepChange={(next, current) => {
                            if (current === 2 && (next === 1 || next === 3)) {
                              requestAnimationFrame(() => scrollToTop());
                            }
                          }}
                          clientBoardItems={clientBoardItems}
                          clientBoard={clientBoard}
                        >
                          <Button
                            leftIcon={NewTab}
                            kind="primary"
                            size="medium"
                            color="primary"
                            onClick={() => {
                              setShowSectionsModal(true);
                            }}
                          >
                            Add section
                          </Button>
                        </OnboardingDialog>
                      </Flex>
                    </Flex>
                  </Flex>
                </Box>
                <div id="sections-end" />
              </Box>
            ) : (
              <Box
                className="portalmanager__content__emptystate"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '24px',
                  width: 'calc(100% - 50px)',
                  minHeight: '300px',
                  border: '1px dashed #D0D4E4',
                  borderRadius: '4px',
                }}
              >
                <Flex
                  direction="column"
                  gap={20}
                  style={{
                    marginTop: '-32px',
                  }}
                >
                  <Flex direction="column" gap={4}>
                    <Flex direction="column" gap={8}>
                      <Icon icon={NewTab} size={28} />
                      <Heading type="h3" weight="bold" align="center">
                        Add your first section
                      </Heading>
                    </Flex>
                    <Text type="text2" align="center" color="secondary">
                      Choose a content section to <br />
                      start building your page.
                    </Text>
                  </Flex>
                  <IconButton
                    icon={AddSmall}
                    kind="primary"
                    size="small"
                    color="primary"
                    onClick={() => {
                      setShowSectionsModal(true);
                    }}
                  />
                </Flex>
              </Box>
            )}
          </Box>
        </div>
      </Box>
    </>
  );
}
